import { gql, useQuery } from '@apollo/client';
import IconTrash from '@apollo/icons/default/IconTrash.svg';
import {
  Button,
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardTitle,
  IconButton,
  Spinner,
  Text,
} from '@apollo/orbit';
import React, { useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { Link } from 'react-router-dom';

import { GraphRef, useGraphRef } from 'src/app/graph/hooks/useGraphRef';
import { LazyUrlModal } from 'src/components/common/LazyUrlModal';
import { PermissionGuard } from 'src/components/permissionGuards/permissionGuard/PermissionGuard';
import { RelativeTimestamp } from 'src/components/timestamp/Timestamp';
import Config from 'src/lib/config';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';
import { setOverlayVisibleLocation } from 'src/lib/routing';
import { dateCompare } from 'src/lib/sort';

import { AddSecretModal } from './addSecretModal/AddSecretModal';
import { DeleteSecretModal } from './deleteSecretModal/DeleteSecretModal';

// if a variant has more secrets than the following value we should only show the first few
// and offer to expand to show more
const MAX_VISIBLE_SECRETS = 4;

export const CloudSecretsListFragment = gql`
  fragment CloudSecretsListFragment on GraphVariant {
    id
    router {
      id
      secrets {
        name
        hash
        createdAt
      }
    }
  }
`;

export const SecretsList = ({
  className,
  status,
  isInitiating,
}: {
  className?: string;
  status: GraphQLTypes.RouterStatus | undefined;
  isInitiating: boolean;
}) => {
  const [shouldShowAllSecrets, setShouldShowAllSecrets] = useState(false);

  const graphRef = useGraphRef();
  const location = useLocation();

  const { data, loading } = useQuery<
    GraphQLTypes.SecretsListQuery,
    GraphQLTypes.SecretsListQueryVariables
  >(
    gql`
      query CloudSecretsListQuery($serviceId: ID!, $graphVariant: String!) {
        service(id: $serviceId) {
          id
          variant(name: $graphVariant) {
            id
            router {
              id
              secrets {
                name
                hash
                createdAt
              }
            }
          }
        }
      }
    `,
    {
      variables: {
        serviceId: graphRef?.graphId as string,
        graphVariant: graphRef?.graphVariant as string,
      },
      skip:
        !graphRef ||
        (status === undefined && !isInitiating) ||
        status === GraphQLTypes.RouterStatus.CREATING,
    },
  );
  const secrets = data?.service?.variant?.router?.secrets;

  const visibleSecrets = useMemo(() => {
    const secretsToSort = secrets?.slice() ?? [];
    const sortedSecrets = secretsToSort?.sort((secretA, secretB) => {
      return dateCompare(secretA.createdAt, secretB.createdAt, false);
    });

    return shouldShowAllSecrets
      ? sortedSecrets
      : sortedSecrets?.slice(0, MAX_VISIBLE_SECRETS);
  }, [secrets, shouldShowAllSecrets]);

  return (
    <>
      <div className={className}>
        <Card>
          <CardHeader>
            <CardTitle>Secrets</CardTitle>
            <PermissionGuard
              graphId={graphRef?.graphId}
              graphVariant={graphRef?.graphVariant}
              graphVariantPermissions="canQueryCloudRouter"
            >
              <CardDescription>
                Here you can enter named values that will be injected as
                environment variables you can reference in the configuration
                below. These variables are stored encrypted and can only be
                accessed by your specific application.
              </CardDescription>
            </PermissionGuard>
            <AddSecretButton
              status={status}
              graphRef={graphRef}
              isInitiating={isInitiating}
              className="hidden md:inline-flex"
            />
            <div className="md:hidden">
              <AddSecretButton
                status={status}
                graphRef={graphRef}
                isInitiating={isInitiating}
              />
            </div>
          </CardHeader>
          <PermissionGuard
            graphId={graphRef?.graphId}
            graphVariant={graphRef?.graphVariant}
            graphVariantPermissions="canQueryCloudRouter"
            fallback={null}
          >
            {loading ? (
              <CardBody className="items-center justify-center">
                <Spinner />
              </CardBody>
            ) : visibleSecrets.length > 0 ? (
              <CardBody className="gap-0">
                {visibleSecrets.map((secret) => (
                  <div
                    key={secret.hash}
                    className="-mx-6 flex items-center justify-between border-t border-t-primary px-6 py-3"
                  >
                    <div className="flex flex-col text-sm">
                      <Text as="div" variant="bodySb">
                        {secret.name}
                      </Text>
                      <div className="text-secondary">
                        Saved{' '}
                        <RelativeTimestamp
                          value={secret.createdAt}
                          tooltipPlacement="bottom"
                        />
                      </div>
                    </div>
                    <PermissionGuard
                      graphId={graphRef?.graphId}
                      graphVariant={graphRef?.graphVariant}
                      graphVariantPermissions="canManageCloudRouter"
                      fallback={null}
                    >
                      <IconButton
                        aria-label="Delete secret"
                        isDisabled={
                          status !== GraphQLTypes.RouterStatus.RUNNING
                        }
                        variant="destructive"
                        as={Link}
                        to={setOverlayVisibleLocation({
                          location: {
                            ...location,
                            state: {
                              deleteSecretModalState: {
                                secretName: secret.name,
                              },
                            },
                          },
                          overlayName: Config.modals.deleteSecret,
                        })}
                      >
                        <IconTrash />
                      </IconButton>
                    </PermissionGuard>
                  </div>
                ))}
              </CardBody>
            ) : null}
          </PermissionGuard>
        </Card>
        <PermissionGuard
          graphId={graphRef?.graphId}
          graphVariant={graphRef?.graphVariant}
          graphVariantPermissions="canQueryCloudRouter"
          fallback={null}
        >
          {isMinLengthArray(MAX_VISIBLE_SECRETS + 1, secrets) && (
            <Button
              variant="buttonLink"
              onClick={() => setShouldShowAllSecrets(!shouldShowAllSecrets)}
              className="ml-2 mt-4"
            >
              {shouldShowAllSecrets ? 'Show fewer secrets' : 'Show all secrets'}
            </Button>
          )}
        </PermissionGuard>
      </div>
      <LazyUrlModal name={Config.modals.deleteSecret}>
        {({ closeModal, isOpen }) => (
          <DeleteSecretModal isOpen={isOpen} onClose={closeModal} />
        )}
      </LazyUrlModal>
      <LazyUrlModal name={Config.modals.addSecret}>
        {({ closeModal, isOpen }) => (
          <AddSecretModal isOpen={isOpen} onClose={closeModal} />
        )}
      </LazyUrlModal>
    </>
  );
};

const AddSecretButton = ({
  className,
  status,
  isInitiating,
  graphRef,
}: {
  className?: string;
  status?: GraphQLTypes.RouterStatus;
  isInitiating: boolean;
  graphRef: GraphRef | null;
}) => {
  const location = useLocation();

  return (
    <PermissionGuard
      graphId={graphRef?.graphId}
      graphVariant={graphRef?.graphVariant}
      graphVariantPermissions="canQueryCloudRouter"
      fallback={null}
    >
      <Button
        className={className}
        variant="secondary"
        isDisabled={
          (status === undefined && !isInitiating) ||
          status !== GraphQLTypes.RouterStatus.RUNNING
        }
        as={Link}
        to={setOverlayVisibleLocation({
          location,
          overlayName: Config.modals.addSecret,
        })}
      >
        Save a secret
      </Button>
    </PermissionGuard>
  );
};
